import ArrowCircle from "@GeriMed/Icons/ArrowCircle";
import ProductDescription, { ProductDescriptionStyles } from "@GeriMed/Overrides/Components/ProductDescription";
import themeColors, { themeFontsSize, themeFontWeights } from "@GeriMed/styles/variables";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    session: state.context.session,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    carouselTitle?: string;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    productViewDetailsBtn?: InjectableCss;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    formularyLockoutMessage?: TypographyProps;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css``,
    },
    productBrandStyles: {
        nameText: {
            color: themeColors.neroColor,
            size: themeFontsSize.size75,
            weight: themeFontWeights.semiBold,
        },
        nameLink: {
            color: themeColors.lightBlue,
            typographyProps: {
                size: themeFontsSize.size75,
                weight: themeFontWeights.semiBold,
            }
        }
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            margin-bottom: 6px;
            margin-top: auto;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    productViewDetailsBtn: {
        css: css`
            > a {
                display: inline-block;
                width: 100%;
                text-align: right;
                &.arrow-button.rightArrow svg {
                    width: 20px;
                }
            }
            
        `
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;

            .qty-wrap {
                display: block;
                [class*="FormFieldStyle"] {
                    width: 60%;
                    margin-left: 10px;
                }
                input:not(.react-datetime-picker__inputGroup__input) {
                    margin-bottom: 0;
                }
            }
        `,
    },
    qtyGridItem: { width: 12 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 12 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    formularyLockoutMessage: {
        size: themeFontsSize.size14,
        color: themeColors.drakYellow,
        css: css`
            background: #fff !important;
            box-shadow: none !important;
            padding: 8px 5px;
            margin-bottom: 10px;
            line-height: normal;
            color: #df8d00 !important;
            text-transform: capitalize;
            width: 100%;
            text-wrap: nowrap;
        `
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    session,
    carouselTitle,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });
    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const productDetailPath = productContext.productInfo.productDetailPath;
    const isRequestAsFormulary = !product.canAddToCart && product.properties['customerHasFormularyLockout'] ==='true';
    const isReverseFormularyLocked = !product.canAddToCart && product.properties['reverseFormularyLocked'] ==='true';
    return (
        <>
            <ProductContext.Provider value={productContext}>
                {showImage && !showOverlayBadge && (
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                    />
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                        {...styles.badgeAreaWrapper}
                        ref={badgeAreaWrapperRef}
                        data-test-selector="productCarouselBadge"
                    >
                        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {showBrand && product.brand && carouselTitle?.toLowerCase() === "featured equipment" && (
                    <StyledWrapper className="product-brand" {...styles.productBrandWrapper}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}
                {showTitle && (
                    <ProductDescription
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productDescriptionStyles}
                        carouselTitle={carouselTitle}
                    />
                )}
                {showBrand && product.brand && carouselTitle?.toLowerCase() !== "featured equipment" && (
                    <StyledWrapper className="product-brand" {...styles.productBrandWrapper}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}

                {showPartNumbers && (
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        showCustomerName={false}
                        showManufacturerItem={true}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                )}
                {showTextBadges && textBadges && (
                    <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                )}
                {showImageBadges && imageBadges && (
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                )}
                {showPrice && (
                    <StyledWrapper {...styles.productPriceWrapper}>
                        <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        <ProductPrice showLabel={false} product={productContext} />
                        {/* {product.unitOfMeasures && (
                            <ProductUnitOfMeasureSelect
                                labelOverride=""
                                extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                id={`${carouselId}-uom`}
                            />
                        )} */}
                        <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                    </StyledWrapper>
                )}
                {isRequestAsFormulary && !isReverseFormularyLocked && (
                    <Typography {...styles.formularyLockoutMessage}>
                        {siteMessage("GM_Non_Formulary_Item_Msg")}
                    </Typography>
                )}
                {isReverseFormularyLocked && (
                    <Typography {...styles.formularyLockoutMessage}>
                        {siteMessage("GM_ReverseFormularyLockout_Msg")}
                    </Typography>
                )}
                {!isAuthenticated && (
                    <StyledWrapper className="btn-action" {...styles.productViewDetailsBtn}>
                        <a href={productDetailPath} className="arrow-button rightArrow">
                            <ArrowCircle/>
                        </a>
                    </StyledWrapper>
                )}
                {showAddToCart && (
                    <GridContainer {...styles.addToCartContainer}>
                        {isAuthenticated && (
                            <GridItem className="qty-wrap" {...styles.qtyGridItem}>
                                {product.canAddToCart &&(
                                <Typography as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                    {translate("Qty:")}
                                </Typography>
                                )}
                                <ProductQuantityOrdered
                                    labelOverride=""
                                    className="qty-input"
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    id={`${carouselId}-qty`}
                                />
                            </GridItem>
                        )}
                        <GridItem className="add-to-cart-wrap" {...styles.addToCartButtonGridItem}>
                            <ProductAddToCartButton
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </GridItem>
                    </GridContainer>
                )}
                {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
